import React, { useMemo } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DateRange, SeriesSet } from '../../types/dashboard';
import { Panel } from '../ui/Panel';
import { ChartTooltip, LegendItem } from '../ui/ChartTooltip';
import { CHART, SOURCE_META } from '../../utils/theme';
import { monthLabel, rangeMonths } from '../../utils/calendar';
import { formatAxis, formatValue, isCountMetric, metricMeta, monthlyBasis, monthValue, type TrendMetric } from '../../utils/metricConfig';
interface TrendPanelProps {
  metric: TrendMetric;
  series: SeriesSet;
  network: SeriesSet;
  range: DateRange;
  scopeUnits: (m: number) => number;
  networkUnits: (m: number) => number;
  unitLabel: 'territory' | 'AOP';
  entityLabel: string;
  showScopeAvg: boolean;
  onMonthClick?: (m: number) => void;
}
interface ChartState {
  activeTooltipIndex?: number;
}
const AXIS_WIDTH = 60;
export function TrendPanel({
  metric,
  series,
  network,
  range,
  scopeUnits,
  networkUnits,
  unitLabel,
  entityLabel,
  showScopeAvg,
  onMonthClick
}: TrendPanelProps) {
  const count = isCountMetric(metric);
  const meta = metricMeta(metric);
  const data = useMemo(() => rangeMonths(range).map(m => {
    const total = monthValue(metric, series.all, m);
    const su = scopeUnits(m);
    const nu = networkUnits(m);
    const net = monthValue(metric, network.all, m);
    return {
      m,
      label: monthLabel(m),
      Digital: monthValue(metric, series.Digital, m),
      Local: monthValue(metric, series.Local, m),
      Unknown: monthValue(metric, series.Unknown, m),
      scope: total,
      network: net,
      scopeAvg: count && su && total !== null ? total / su : null,
      networkAvg: count && nu && net !== null ? net / nu : null
    };
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [metric, series, network, range]);
  const fmt = (v: number) => formatValue(metric, v);
  const axisFmt = (v: number) => formatAxis(metric, v);
  const axis = {
    tick: {
      fontSize: 11.5,
      fill: CHART.axis
    },
    axisLine: false,
    tickLine: false
  } as const;
  const handleClick = (state: ChartState | null) => {
    if (!onMonthClick || !state || state.activeTooltipIndex === undefined) return;
    onMonthClick(range.start + state.activeTooltipIndex);
  };
  const cursor = onMonthClick ? 'pointer' : undefined;
  return <Panel title={`${meta.label} — 13-month trend`} definitionId={meta.definitionId} subtitle={`${range.label} · ${entityLabel} · ${monthlyBasis(metric)}${onMonthClick ? ' · click a month for detail' : ''}`} data-id="element-17" data-mp="xk2a778x8wz0">
      {count ? <>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2" data-id="element-18" data-mp="oht4cuollrry">
            <h3 className="text-xs font-semibold text-navy" data-id="element-19" data-mp="349qq4acx7lev">
              {metric === 'members' ? 'New members' : `Total ${meta.label.toLowerCase()}`} · {entityLabel} total · stacked by source
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1" data-id="element-20" data-mp="31oixuwia5byn">
              {SOURCE_META.map(s => <LegendItem key={s.key} color={s.color} label={s.label} data-id="element-21" data-mp="2wqps24kw2jyw" />)}
              <span className="text-[11px] text-ink-subtle" data-id="element-22" data-mp="3aq8oi04c86vd">Source mapping illustrative</span>
            </div>
          </div>
          <div className="h-[270px]" data-id="element-23" data-mp="35pl25rr20e7h">
            <ResponsiveContainer width="100%" height="100%" data-id="element-24" data-mp="2i1l5ycaz3vli">
              <ComposedChart data={data} syncId="trend" margin={{
            top: 6,
            right: 8,
            left: 0,
            bottom: 0
          }} onClick={handleClick} barCategoryGap="22%" data-id="element-25" data-mp="2hzqm71bfu9j0">
                <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-26" data-mp="2rsy05ak8uxup" />
                <XAxis dataKey="label" {...axis} axisLine={{
              stroke: CHART.grid
            }} data-id="element-27" data-mp="2n1fyekaect8o" />
                <YAxis tickFormatter={axisFmt} width={AXIS_WIDTH} {...axis} label={{
              value: 'Total',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: {
                fontSize: 11,
                fill: CHART.muted
              }
            }} data-id="element-28" data-mp="1zbloaahgpis4" />
                <Tooltip cursor={{
              fill: 'rgba(20,48,92,0.05)'
            }} content={<ChartTooltip formatter={v => fmt(v)} footer={onMonthClick ? 'Click for month detail' : undefined} data-id="element-30" data-mp="2czdno0i0zkwh" />} data-id="element-29" data-mp="27x4up455kiud" />
                {SOURCE_META.map((s, i) => <Bar key={s.key} dataKey={s.key} name={s.label} stackId="src" fill={s.color} radius={i === SOURCE_META.length - 1 ? [3, 3, 0, 0] : undefined} maxBarSize={46} isAnimationActive={false} cursor={cursor} data-id="element-31" data-mp="2mnvmic6qvhxh" data-mpi={i} />)}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 border-t border-line pt-3" data-id="element-32" data-mp="16aniye9blu1k">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2" data-id="element-33" data-mp="16c8di3vmxczf">
              <h3 className="text-xs font-semibold text-navy" data-id="element-34" data-mp="1b98i0acu67of">
                Avg {metric === 'members' ? 'new members' : meta.label.toLowerCase()} per active {unitLabel} · monthly total ÷ active {unitLabel === 'AOP' ? 'AOPs' : 'territories'} that month · own scale
              </h3>
              <div className="flex flex-wrap gap-x-4 gap-y-1" data-id="element-35" data-mp="3frg37r1m3mhv">
                {showScopeAvg && <LegendItem color={CHART.navy} label={`${entityLabel} avg`} line data-id="element-36" data-mp="3aswl61q52uus" />}
                <LegendItem color={CHART.accent} label={`Network avg per active ${unitLabel}`} line dashed data-id="element-37" data-mp="bczv9u1rcsbf" />
              </div>
            </div>
            <div className="h-[120px]" data-id="element-38" data-mp="7bwixhix5eey">
              <ResponsiveContainer width="100%" height="100%" data-id="element-39" data-mp="2biiakjnd6qy">
                <LineChart data={data} syncId="trend" margin={{
              top: 6,
              right: 8,
              left: 0,
              bottom: 0
            }} onClick={handleClick} data-id="element-40" data-mp="2e4oogusxg7l">
                  <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-41" data-mp="2n1zilfk8h9w7" />
                  <XAxis dataKey="label" {...axis} axisLine={{
                stroke: CHART.grid
              }} tick={false} height={4} data-id="element-42" data-mp="2n06b5y1sqln5" />
                  <YAxis tickFormatter={axisFmt} width={AXIS_WIDTH} {...axis} domain={['auto', 'auto']} tickCount={3} label={{
                value: 'Avg',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: {
                  fontSize: 11,
                  fill: CHART.muted
                }
              }} data-id="element-43" data-mp="2jkbrjn716hoq" />
                  <Tooltip content={<ChartTooltip formatter={v => fmt(v)} data-id="element-45" data-mp="2fkkux2utp7ca" />} data-id="element-44" data-mp="2fgx0m1hk0gwi" />
                  {showScopeAvg && <Line type="monotone" dataKey="scopeAvg" name={`${entityLabel} avg`} stroke={CHART.navy} strokeWidth={2} dot={{
                r: 2.5,
                fill: CHART.navy
              }} isAnimationActive={false} data-id="element-46" data-mp="20siyxtvviyhj" />}
                  <Line type="monotone" dataKey="networkAvg" name={`Network avg / ${unitLabel}`} stroke={CHART.accent} strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} data-id="element-47" data-mp="1ruqj8om8potm" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </> : <>
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-48" data-mp="1mvx30ofkjjpr">
            <LegendItem color={CHART.navy} label={showScopeAvg ? entityLabel : 'Network (all territories)'} line data-id="element-49" data-mp="138j24czjw1yc" />
            {showScopeAvg && <LegendItem color={CHART.accent} label="Network benchmark" line dashed data-id="element-50" data-mp="1d6go0ttxtmns" />}
            {SOURCE_META.map(s => <LegendItem key={s.key} color={s.color} label={s.label} line data-id="element-51" data-mp="18743ifqjgrod" />)}
          </div>
          <div className="h-[320px]" data-id="element-52" data-mp="1m6d6uetnbrax">
            <ResponsiveContainer width="100%" height="100%" data-id="element-53" data-mp="1m5by4h03rgwd">
              <LineChart data={data} margin={{
            top: 8,
            right: 12,
            left: 0,
            bottom: 0
          }} onClick={handleClick} data-id="element-54" data-mp="1h70ljyifasag">
                <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-55" data-mp="1vvn0hz451i15" />
                <XAxis dataKey="label" {...axis} axisLine={{
              stroke: CHART.grid
            }} data-id="element-56" data-mp="1qxbr1phr99jg" />
                <YAxis tickFormatter={axisFmt} width={AXIS_WIDTH} {...axis} domain={metric === 'conversion' ? [0, 'auto'] : ['auto', 'auto']} data-id="element-57" data-mp="24xd2fyvnbzd6" />
                <Tooltip content={<ChartTooltip formatter={v => fmt(v)} footer={onMonthClick ? 'Click for month detail' : undefined} data-id="element-59" data-mp="20xvw738eay6y" />} data-id="element-58" data-mp="20xcarcjjdro6" />
                {SOURCE_META.map(s => <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} data-id="element-60" data-mp="3c90syv8pq57c" />)}
                {showScopeAvg && <Line type="monotone" dataKey="network" name="Network benchmark" stroke={CHART.accent} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} data-id="element-61" data-mp="3la8hwmpc9tor" />}
                <Line type="monotone" dataKey="scope" name={showScopeAvg ? entityLabel : 'Network (all territories)'} stroke={CHART.navy} strokeWidth={2.5} dot={{
              r: 3,
              fill: CHART.navy
            }} connectNulls isAnimationActive={false} cursor={cursor} data-id="element-62" data-mp="2thhsav25v38h" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>}
    </Panel>;
}